/**
 * 페이지별 상세 결과 화면
 * - 페이지 통계
 * - 정답/오답 문제 목록
 * - 문제별 시도 이력
 * - 재채점 허락/취소 버튼
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Row,
  Col,
  Table,
  Tag,
  Button,
  Spin,
  Alert,
  Space,
  Collapse,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  LeftOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { getStudentResults, allowRetry, revokeRetry, getPendingGrading, gradeSubmission } from '../../services/gradingService';
import type { StudentResult, PageResult, WrongProblem, PendingGradingItem } from '../../types/grading';
import { ManualGradingModal } from '../../components/grading/ManualGradingModal';
import { formatKoreanDateTime } from '../../utils/timeUtils';

const { Title, Text } = Typography;
const { Panel } = Collapse;

export const PageResultDetailPage: React.FC = () => {
  const { studentId, workbook, page } = useParams<{
    studentId: string;
    workbook: string;
    page: string;
  }>();
  const navigate = useNavigate();

  // 상태 관리
  const [result, setResult] = useState<StudentResult | null>(null);
  const [pageResult, setPageResult] = useState<PageResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // T타입 수동 채점 모달
  const [gradingModalVisible, setGradingModalVisible] = useState(false);
  const [currentGradingItem, setCurrentGradingItem] = useState<PendingGradingItem | null>(null);
  const [pendingItems, setPendingItems] = useState<PendingGradingItem[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);

  /**
   * 학생 결과 조회
   */
  useEffect(() => {
    if (!studentId || !workbook || !page) return;

    const fetchResult = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getStudentResults(studentId, workbook);
        setResult(data);

        // 해당 페이지 결과 찾기
        const targetPage = data.pages.find((p) => p.page === parseInt(page));
        setPageResult(targetPage || null);
      } catch (err: any) {
        console.error('결과 조회 실패:', err);
        setError(err.response?.data?.detail || '결과 조회에 실패했습니다');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [studentId, workbook, page]);

  /**
   * 재채점 허락
   */
  const handleAllowRetry = async () => {
    if (!studentId || !workbook || !page || !pageResult) return;

    try {
      setActionLoading(true);

      const response = await allowRetry({
        student_id: studentId,
        workbook_name: workbook,
        page: parseInt(page),
      });

      message.success(response.message);

      // 결과 새로고침
      const data = await getStudentResults(studentId, workbook);
      const targetPage = data.pages.find((p) => p.page === parseInt(page));
      setPageResult(targetPage || null);
    } catch (err: any) {
      message.error(err.response?.data?.detail || '재채점 허락 실패');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * 재채점 권한 취소
   */
  const handleRevokeRetry = async () => {
    if (!studentId || !workbook || !page || !pageResult) return;

    try {
      setActionLoading(true);

      const response = await revokeRetry({
        student_id: studentId,
        workbook_name: workbook,
        page: parseInt(page),
      });

      message.success(response.message);

      // 결과 새로고침
      const data = await getStudentResults(studentId, workbook);
      const targetPage = data.pages.find((p) => p.page === parseInt(page));
      setPageResult(targetPage || null);
    } catch (err: any) {
      message.error(err.response?.data?.detail || '재채점 권한 취소 실패');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * 오답 문제 정렬 (문제 번호 오름차순)
   */
  const getSortedWrongProblems = () => {
    if (!pageResult) return [];

    return [...pageResult.wrong_problem_list].sort((a, b) => {
      // 문제 번호 오름차순
      return parseInt(a.problem_number) - parseInt(b.problem_number);
    });
  };

  /**
   * T타입 채점 시작
   */
  const handleStartGrading = async () => {
    if (!studentId || !workbook || !page) return;

    try {
      // 해당 페이지의 채점 대기 목록 조회
      const response = await getPendingGrading(studentId, workbook, parseInt(page));

      if (response.items.length === 0) {
        message.info('채점 대기 중인 문제가 없습니다');
        return;
      }

      setPendingItems(response.items);
      setCurrentItemIndex(0);
      setCurrentGradingItem(response.items[0]);
      setGradingModalVisible(true);
    } catch (err: any) {
      message.error('채점 대기 목록 조회 실패');
    }
  };

  /**
   * 채점 완료 후 처리
   */
  const handleGraded = async () => {
    // 다음 문제로 이동
    const nextIndex = currentItemIndex + 1;

    if (nextIndex < pendingItems.length) {
      // 다음 채점 대기 문제로 이동
      setCurrentItemIndex(nextIndex);
      setCurrentGradingItem(pendingItems[nextIndex]);
    } else {
      // 모든 채점 완료
      setGradingModalVisible(false);
      message.success('모든 T타입 문제 채점이 완료되었습니다!');
    }

    // 결과 새로고침
    const data = await getStudentResults(studentId!, workbook!);
    const targetPage = data.pages.find((p) => p.page === parseInt(page!));
    setPageResult(targetPage || null);
  };

  /**
   * 강사가 오답을 정답으로 직접 변경
   */
  const handleOverrideToCorrect = async (submissionId: string, problemNumber: string) => {
    if (!window.confirm(`문제 ${problemNumber}번을 정답 처리하시겠습니까?`)) {
      return;
    }

    try {
      setActionLoading(true);

      await gradeSubmission(submissionId, true);
      message.success(`문제 ${problemNumber}번을 정답 처리했습니다`);

      // 결과 새로고침
      const data = await getStudentResults(studentId!, workbook!);
      const targetPage = data.pages.find((p) => p.page === parseInt(page!));
      setPageResult(targetPage || null);
    } catch (err: any) {
      message.error(err.response?.data?.detail || '정답 처리 실패');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * 채점 모달 닫기
   */
  const handleCloseGradingModal = () => {
    setGradingModalVisible(false);
    setCurrentGradingItem(null);
  };

  /**
   * 돌아가기 - 쿼리 파라미터 유지
   */
  const handleGoBack = () => {
    if (studentId && workbook) {
      navigate(`/grading?student=${studentId}&workbook=${encodeURIComponent(workbook)}`);
    } else {
      navigate('/grading');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message="오류" description={error} type="error" showIcon />;
  }

  if (!result || !pageResult) {
    return <Alert message="결과를 찾을 수 없습니다" type="warning" showIcon />;
  }

  // 시도 이력 테이블 컬럼
  const attemptColumns = [
    {
      title: '시도',
      dataIndex: 'attempt_number',
      key: 'attempt_number',
      render: (attempt: number) => `${attempt}차`,
    },
    {
      title: '제출 답안',
      dataIndex: 'user_answer',
      key: 'user_answer',
    },
    {
      title: '결과',
      dataIndex: 'is_correct',
      key: 'is_correct',
      render: (isCorrect: boolean | null) => {
        if (isCorrect === true) {
          return <Tag color="green">정답</Tag>;
        } else if (isCorrect === false) {
          return <Tag color="red">오답</Tag>;
        } else {
          return <Tag color="orange">채점 대기</Tag>;
        }
      },
    },
    {
      title: '점수',
      dataIndex: 'score',
      key: 'score',
      render: (score: number) => `${score}점`,
    },
    {
      title: '제출 시간',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      render: (time: string) => formatKoreanDateTime(time),
    },
  ];

  return (
    <div>
      {/* 헤더 */}
      <div style={{ marginBottom: 24 }}>
        <Button icon={<LeftOutlined />} onClick={handleGoBack} style={{ marginBottom: 16 }}>
          돌아가기
        </Button>
        <Title level={2}>
          {result.student_name}님 - {result.workbook_name} - {page}페이지 상세 결과
        </Title>
      </div>

      {/* 페이지 통계 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Text type="secondary">정답률</Text>
            <div>
              <Text
                strong
                style={{
                  fontSize: '32px',
                  color:
                    pageResult.accuracy >= 80
                      ? '#52c41a'
                      : pageResult.accuracy >= 60
                      ? '#faad14'
                      : '#ff4d4f',
                }}
              >
                {pageResult.accuracy}%
              </Text>
            </div>
            <Text type="secondary">
              {pageResult.correct_problems}/{pageResult.total_problems} 정답
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Text type="secondary">오답 문제</Text>
            <div>
              <Text strong style={{ fontSize: '32px', color: '#ff4d4f' }}>
                {pageResult.wrong_problems}
              </Text>
            </div>
            {pageResult.wrong_problem_list.length > 0 && (
              <Text type="secondary">
                평균{' '}
                {(
                  pageResult.wrong_problem_list.reduce(
                    (sum, p) => sum + p.attempts.length,
                    0
                  ) / pageResult.wrong_problem_list.length
                ).toFixed(1)}
                회 시도
              </Text>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Text type="secondary">채점 대기</Text>
            <div>
              <Text strong style={{ fontSize: '32px', color: '#faad14' }}>
                {pageResult.pending_problems}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Text type="secondary">미제출 문제</Text>
            <div>
              <Text strong style={{ fontSize: '32px', color: '#8c8c8c' }}>
                {pageResult.not_submitted_problem_numbers.length}
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Text type="secondary">재채점 상태</Text>
            <div style={{ marginTop: 8 }}>
              {pageResult.retry_allowed ? (
                <Tag color="green" icon={<CheckCircleOutlined />}>
                  허락됨
                </Tag>
              ) : (
                <Tag color="default" icon={<CloseCircleOutlined />}>
                  불허
                </Tag>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* 재채점 관리 */}
      {pageResult.wrong_problems > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <Space>
            <Text strong>재채점 권한 관리</Text>
            {!pageResult.retry_allowed ? (
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleAllowRetry}
                loading={actionLoading}
              >
                재채점 허락 ({pageResult.wrong_problems}개 오답)
              </Button>
            ) : (
              <Button
                danger
                icon={<StopOutlined />}
                onClick={handleRevokeRetry}
                loading={actionLoading}
              >
                재채점 권한 취소
              </Button>
            )}
          </Space>
        </Card>
      )}

      {/* 오답 문제 이력 (오답이었던 문제 포함) */}
      {pageResult.wrong_problem_list.length > 0 && (
        <Card title="문제 이력 (오답이었던 문제)" style={{ marginBottom: 24 }}>
          <Collapse defaultActiveKey={[getSortedWrongProblems()[0]?.problem_number]}>
            {getSortedWrongProblems().map((problem) => {
              // 학습 개선도 계산
              const firstAttempt = problem.attempts[0];
              const lastAttempt = problem.attempts[problem.attempts.length - 1];
              const isImproving = problem.attempts.length > 1 && lastAttempt.is_correct === true;
              const isStillWrong = lastAttempt.is_correct === false;

              return (
                <Panel
                  header={
                    <Space>
                      <Text strong>문제 {problem.problem_number}번</Text>
                      <Tag color={problem.attempts.length > 2 ? 'red' : 'orange'}>
                        {problem.attempts.length}회 시도
                      </Tag>
                      {isImproving && <Tag color="green">✅ 개선됨</Tag>}
                      {isStillWrong && <Tag color="red">❌ 여전히 오답</Tag>}
                      {isStillWrong && (
                        <Button
                          size="small"
                          type="primary"
                          onClick={(e) => {
                            e.stopPropagation(); // Panel 접기/펼치기 방지
                            handleOverrideToCorrect(lastAttempt.submission_id, problem.problem_number);
                          }}
                          loading={actionLoading}
                        >
                          정답 처리
                        </Button>
                      )}
                      <Text type="secondary">정답: {problem.correct_answer}</Text>
                      <Text type="secondary">
                        최근: {formatKoreanDateTime(lastAttempt.submitted_at)}
                      </Text>
                    </Space>
                  }
                  key={problem.problem_number}
                >
                  <Table
                    dataSource={problem.attempts}
                    columns={attemptColumns}
                    pagination={false}
                    size="small"
                    rowKey="attempt_number"
                    rowClassName={(record) =>
                      record.attempt_number === lastAttempt.attempt_number
                        ? 'bg-blue-50'
                        : ''
                    }
                  />
                </Panel>
              );
            })}
          </Collapse>
        </Card>
      )}

      {/* 정답 문제 정보 */}
      {pageResult.correct_problems > 0 && (
        <Card title="정답 문제" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 12 }}>
            <Text>
              정답을 맞힌 문제: <Text strong>{pageResult.correct_problems}개</Text>
            </Text>
          </div>
          <div>
            <Text type="secondary">문제 번호: </Text>
            {pageResult.correct_problem_numbers.map((num) => (
              <Tag key={num} color="green" style={{ marginBottom: 4 }}>
                {num}번
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {/* 미제출 문제 정보 */}
      {pageResult.not_submitted_problem_numbers.length > 0 && (
        <Card title="미제출 문제" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 12 }}>
            <Text>
              제출하지 않은 문제: <Text strong>{pageResult.not_submitted_problem_numbers.length}개</Text>
            </Text>
          </div>
          <div>
            <Text type="secondary">문제 번호: </Text>
            {pageResult.not_submitted_problem_numbers.map((num) => (
              <Tag key={num} color="default" style={{ marginBottom: 4 }}>
                {num}번
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {/* 채점 대기 정보 (T타입) */}
      {pageResult.pending_problems > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <Alert
            message="채점 대기 중인 문제가 있습니다"
            description={`${pageResult.pending_problems}개의 서술형 문제가 채점 대기 중입니다.`}
            type="warning"
            icon={<ClockCircleOutlined />}
            showIcon
            action={
              <Button type="primary" onClick={handleStartGrading}>
                T타입 채점하기
              </Button>
            }
          />
        </Card>
      )}

      {/* T타입 수동 채점 모달 */}
      <ManualGradingModal
        item={currentGradingItem}
        visible={gradingModalVisible}
        onClose={handleCloseGradingModal}
        onGraded={handleGraded}
      />
    </div>
  );
};
